import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useSettings } from '../context/SettingsContext';
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

const API_URL =
    process.env.REACT_APP_API_URL ||
    "https://store.mvtyres.com/backend/api/";

// Load Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_YOUR_KEY_HERE');

/* ---------------------- PAYMENT ASSIST (PAY IN 3) ------------------------ */

const PaymentAssist = ({ total, orderNumber, orders }) => {
    const [loading, setLoading] = useState(false);
    const { settings } = useSettings();

    const shipping = settings?.shipping_rate;
    const safeSubtotal = Number(total || 0);
    const safeTotal = safeSubtotal + shipping; // subtotal + shipping
    const installment = (safeTotal / 3).toFixed(2);

    const [form, setForm] = useState({
        full_name: `${orders?.fname || orders?.first_name || ""} ${orders?.lname || orders?.last_name || ""
            }`,
        email: orders?.email || "",
        phone: orders?.phone || "",
        address: orders?.address1 || "",
        dob: "", // HTML date input: YYYY-MM-DD
    });

    const handleAssitIn = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const submitAssist = async () => {
        if (!form.dob) {
            toast.error("Enter your Date of Birth");
            return;
        }

        // full_name ko first_name / last_name me tod do
        let firstName = orders?.fname || orders?.first_name || "";
        let lastName = orders?.lname || orders?.last_name || "";

        if (form.full_name && form.full_name.trim().length > 0) {
            const parts = form.full_name.trim().split(" ");
            firstName = parts[0];
            lastName = parts.slice(1).join(" ") || parts[0];
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API_URL}payment-assist/create`, {
                order_number: orderNumber,
                amount: safeTotal, // backend pence me convert karega
                first_name: firstName,
                last_name: lastName,
                address_line_1: form.address || orders.address1,
                address_line_2: orders.address2,
                town: orders.town,
                county: orders.state,
                postcode: orders.zip,
                email: form.email || orders.email,
                phone: form.phone || orders.phone,
                dob: form.dob,
            });

            if (res.data?.status && res.data?.data?.redirect_url) {
                toast.success("Redirecting to Payment Assist...");
                window.location.href = res.data.data.redirect_url;
            } else {
                toast.error(
                    res.data?.message || "Unable to start Payment Assist session"
                );
            }
        } catch (err) {
            console.error("Payment Assist Error:", err);
            const apiError = err.response?.data;
            const telephoneError =
                apiError?.errors?.data?.telephone ||
                apiError?.errors?.data?.phone;
            toast.error(
                telephoneError ||
                apiError?.message ||
                "Payment Assist error"
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="payment-card fadeArea">
            <h2 className="payment-title">Pay in 3 Monthly Installments</h2>
            <p className="payment-amount">
                Total (incl. delivery):{" "}
                <strong>£{safeTotal.toFixed(2)}</strong>
            </p>
            <p className="installment-txt">
                You will pay <strong>£{installment}</strong> per month for 3
                months.
            </p>

            <div className="row">
                <div className="col-lg-6">
                    <label>Enter Your Name</label>
                    <input
                        type="text"
                        name="full_name"
                        placeholder="FULL NAME"
                        value={form.full_name}
                        onChange={handleAssitIn}
                        className="pay-input"
                    />
                </div>
                <div className="col-lg-6">
                    <label>Enter Your Email</label>
                    <input
                        type="email"
                        name="email"
                        placeholder="EMAIL ADDRESS"
                        value={form.email}
                        onChange={handleAssitIn}
                        className="pay-input"
                    />
                </div>
                <div className="col-lg-12">
                    <label>Enter Your Address</label>
                    <input
                        type="text"
                        name="address"
                        placeholder="ADDRESS"
                        value={form.address}
                        onChange={handleAssitIn}
                        className="pay-input"
                    />
                </div>
                <div className="col-lg-6">
                    <label>Enter Your Number</label>
                    <input
                        type="text"
                        name="phone"
                        placeholder="PHONE NUMBER"
                        value={form.phone}
                        onChange={handleAssitIn}
                        className="pay-input"
                    />
                </div>
                <div className="col-lg-6">
                    <label>Enter Your DOB</label>
                    <input
                        type="date"
                        name="dob"
                        className="pay-input"
                        value={form.dob}
                        onChange={handleAssitIn}
                    />
                </div>
            </div>

            <button
                className="theme-btn modern-btn"
                onClick={submitAssist}
                disabled={loading}
            >
                {loading ? "Processing..." : "Continue to Payment Assist"}
            </button>
        </div>
    );
};

/* ---------------------- STRIPE CARD PAYMENT ------------------------ */

const StripePaymentForm = ({ total, orderNumber, orders }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const { settings } = useSettings();
    const navigate = useNavigate();

    const shipping = settings?.shipping_rate || 0;
    const safeSubtotal = Number(total || 0);
    const safeTotal = safeSubtotal + shipping;

    useEffect(() => {
        let cancelled = false;

        const createPaymentIntent = async () => {
            try {
                const res = await axios.post(`${API_URL}stripe/create-intent`, {
                    order_number: orderNumber
                });

                if (cancelled) return;

                if (res.data?.status && res.data?.clientSecret) {
                    setClientSecret(res.data.clientSecret);
                } else {
                    toast.error(res.data?.message || "Failed to initialize payment");
                }
            } catch (err) {
                if (cancelled) return;
                console.error("Stripe Intent Error:", err);
                toast.error(err.response?.data?.message || "Payment initialization failed");
            }
        };

        if (orderNumber && !clientSecret) {
            createPaymentIntent();
        }

        return () => { cancelled = true; };
    }, [orderNumber]);

    const handleStripePayment = async (e) => {
        e.preventDefault();

        if (!stripe || !elements || !clientSecret) {
            toast.error("Stripe is not ready yet. Please wait...");
            return;
        }

        setLoading(true);

        try {
            const cardElement = elements.getElement(CardElement);

            const { error, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                {
                    payment_method: {
                        card: cardElement,
                        billing_details: {
                            name: `${orders?.fname || orders?.first_name || ""} ${orders?.lname || orders?.last_name || ""}`,
                            email: orders?.email || "",
                            phone: orders?.phone || "",
                            address: {
                                line1: orders?.address1 || "",
                                line2: orders?.address2 || "",
                                city: orders?.town || "",
                                state: orders?.state || "",
                                postal_code: orders?.zip || "",
                                country: "GB",
                            },
                        },
                    },
                }
            );

            if (error) {
                toast.error(error.message || "Payment failed");
                setLoading(false);
                return;
            }

            if (paymentIntent.status === "succeeded") {
                // Finalize payment on backend
                const finalizeRes = await axios.post(`${API_URL}stripe/finalize`, {
                    order_number: orderNumber
                });

                if (finalizeRes.data?.status) {
                    toast.success("Payment successful!");

                    // Clear cart
                    localStorage.removeItem("cart");
                    window.dispatchEvent(new Event("cartUpdated"));

                    // Redirect to thank you page
                    setTimeout(() => {
                        navigate("/thankyou");
                    }, 1500);
                } else {
                    toast.error(finalizeRes.data?.message || "Payment verification failed");
                }
            }
        } catch (err) {
            console.error("Stripe Payment Error:", err);
            toast.error(err.response?.data?.message || "Payment processing failed");
        } finally {
            setLoading(false);
        }
    };

    const cardElementOptions = {
        style: {
            base: {
                fontSize: "16px",
                color: "#32325d",
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                "::placeholder": {
                    color: "#aab7c4",
                },
            },
            invalid: {
                color: "#fa755a",
                iconColor: "#fa755a",
            },
        },
        hidePostalCode: true,
    };

    return (
        <div className="payment-card fadeArea">
            <h2 className="payment-title">Pay with Card (Stripe)</h2>
            <p className="payment-amount">
                Total (incl. delivery): <strong>£{safeTotal.toFixed(2)}</strong>
            </p>
            <p className="installment-txt">
                Secure payment powered by Stripe. Your payment will be processed immediately.
            </p>

            <form onSubmit={handleStripePayment}>
                <div className="stripe-card-element">
                    <label>Card Details</label>
                    <div style={{
                        padding: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        marginTop: "8px",
                        backgroundColor: "#fff"
                    }}>
                        <CardElement options={cardElementOptions} />
                    </div>
                </div>

                <button
                    type="submit"
                    className="theme-btn modern-btn"
                    disabled={loading || !stripe || !clientSecret}
                    style={{ marginTop: "20px" }}
                >
                    {loading ? "Processing..." : `Pay £${safeTotal.toFixed(2)}`}
                </button>
            </form>

            <div style={{ marginTop: "15px", fontSize: "12px", color: "#666" }}>
                <i className="fas fa-lock"></i> Your payment information is secure and encrypted.
            </div>
        </div>
    );
};

const StripePayment = (props) => (
    <Elements stripe={stripePromise}>
        <StripePaymentForm {...props} />
    </Elements>
);

/* --------------------------- MAIN PAYMENT MODAL -------------------------- */

const Payment = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { cart, orders, total, orderNumber } = location.state || {};
    const [selectedMethod, setSelectedMethod] = useState("paymentassist");

    useEffect(() => {
        if (!location.state) navigate("/checkout");
    }, [location.state, navigate]);

    if (!cart || !orders || !total || !orderNumber) {
        return null;
    }

    return (
        <div className="payment-popup-container">
            <div className="payment-wrapper">
                <div className="payment-method-selector">
                    <h2 style={{ textAlign: "center", marginBottom: "25px" }}>Choose Payment Method</h2>

                    <div className="payment-tabs">
                        <button
                            className={`payment-tab ${selectedMethod === "paymentassist" ? "active" : ""}`}
                            onClick={() => setSelectedMethod("paymentassist")}
                        >
                            <i className="fas fa-calendar-alt"></i> Pay in 3 Installments
                        </button>
                        <button
                            className={`payment-tab ${selectedMethod === "stripe" ? "active" : ""}`}
                            onClick={() => setSelectedMethod("stripe")}
                        >
                            <i className="fas fa-credit-card"></i> Pay with Card
                        </button>
                    </div>
                </div>

                {selectedMethod === "paymentassist" && (
                    <PaymentAssist
                        total={total}
                        cart={cart}
                        orders={orders}
                        orderNumber={orderNumber}
                    />
                )}

                {selectedMethod === "stripe" && (
                    <StripePayment
                        total={total}
                        cart={cart}
                        orders={orders}
                        orderNumber={orderNumber}
                    />
                )}
            </div>

            <style jsx>{`
                .payment-method-selector {
                    margin-bottom: 30px;
                }

                .payment-tabs {
                    display: flex;
                    gap: 15px;
                    justify-content: center;
                    margin-bottom: 30px;
                }

                .payment-tab {
                    flex: 1;
                    max-width: 300px;
                    padding: 15px 20px;
                    border: 2px solid #ddd;
                    background: white;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 16px;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }

                .payment-tab:hover {
                    border-color: #007bff;
                    background: #f8f9fa;
                }

                .payment-tab.active {
                    border-color: #007bff;
                    background: #007bff;
                    color: white;
                }

                .payment-tab i {
                    font-size: 18px;
                }

                .payment-card {
                    background: white;
                    padding: 30px;
                    border-radius: 10px;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                }

                .payment-title {
                    font-size: 24px;
                    font-weight: 600;
                    margin-bottom: 15px;
                    color: #333;
                }

                .payment-amount {
                    font-size: 18px;
                    margin-bottom: 10px;
                    color: #555;
                }

                .installment-txt {
                    font-size: 14px;
                    color: #666;
                    margin-bottom: 20px;
                }

                @media (max-width: 768px) {
                    .payment-tabs {
                        flex-direction: column;
                    }

                    .payment-tab {
                        max-width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default Payment;
